import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Save, Calendar, CheckCircle, ExternalLink, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { BUSINESS_NAME } from '../../config/brand';

export default function AdminSettings() {
  const [searchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    api.get('/admin/settings').then(r => { setSettings(r.data); setLoading(false); }).catch(() => setLoading(false));
    api.get('/oauth/calendar/status').then(r => setCalendarStatus(r.data)).catch(() => {});
    if (searchParams.get('calendar') === 'connected') {
      toast.success('Google Calendar connected successfully!');
    }
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', settings);
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  const connectCalendar = async () => {
    setConnecting(true);
    try {
      const res = await api.get('/oauth/calendar/connect');
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start calendar connection');
      setConnecting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#6A3E28] border-t-transparent" /></div>;
  if (!settings) return <p>Failed to load settings</p>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-settings-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#3D2418]" style={{ fontFamily: 'Manrope' }}>Business Settings</h1>
        <p className="text-sm text-[#7A6B5E] mt-1">Configure your grooming studio</p>
      </div>

      <Card className="rounded-2xl border-[#E8DDD4] bg-white" style={{ boxShadow: '0 2px 12px rgba(106,62,40,0.06)' }}>
        <CardContent className="p-6 space-y-5">
          <h3 className="text-lg font-semibold text-[#3D2418]">Company Information</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-sm">Company Name</Label><Input value={settings.company_name || ''} onChange={(e) => setSettings({ ...settings, company_name: e.target.value })} className="mt-1 rounded-lg" data-testid="settings-company-name" /></div>
            <div><Label className="text-sm">Phone</Label><Input value={settings.phone || ''} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} className="mt-1 rounded-lg" data-testid="settings-phone" /></div>
            <div><Label className="text-sm">Email</Label><Input value={settings.email || ''} onChange={(e) => setSettings({ ...settings, email: e.target.value })} className="mt-1 rounded-lg" data-testid="settings-email" /></div>
            <div><Label className="text-sm">Address</Label><Input value={settings.address || ''} onChange={(e) => setSettings({ ...settings, address: e.target.value })} className="mt-1 rounded-lg" data-testid="settings-address" /></div>
          </div>
        </CardContent>
      </Card>

      {/* Google Calendar Integration */}
      <Card className="rounded-2xl border-[#E8DDD4] bg-white" style={{ boxShadow: '0 2px 12px rgba(106,62,40,0.06)' }}>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#3D2418]">Google Calendar</h3>
              <p className="text-sm text-[#7A6B5E]">Sync bookings and pet birthdays to your calendar</p>
            </div>
          </div>

          {calendarStatus?.connected ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3" data-testid="calendar-connected">
              <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-green-800">Connected</p>
                <p className="text-xs text-green-700">Syncing to {calendarStatus.email}</p>
              </div>
            </div>
          ) : calendarStatus?.configured ? (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Not connected yet</p>
                  <p className="text-xs text-amber-700">Click below to authorize {BUSINESS_NAME} to access your Google Calendar</p>
                </div>
              </div>
              <Button
                className="rounded-xl bg-blue-600 text-white hover:bg-blue-700 shadow-md"
                onClick={connectCalendar}
                disabled={connecting}
                data-testid="connect-calendar-btn"
              >
                <Calendar className="w-4 h-4 mr-2" />
                {connecting ? 'Connecting...' : 'Connect Google Calendar'}
              </Button>
            </div>
          ) : (
            <div className="bg-[#FBF7F3] border border-[#E8DDD4] rounded-xl p-4 space-y-3" data-testid="calendar-setup-needed">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[#A09387] shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-[#3D2418]">Setup Required</p>
                  <p className="text-xs text-[#7A6B5E]">Google Calendar API credentials need to be configured.</p>
                </div>
              </div>
              <div className="text-sm text-[#5A4E44] space-y-2 pl-8">
                <p className="font-semibold">How to set up:</p>
                <ol className="list-decimal pl-5 space-y-1.5 text-xs">
                  <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-[#6A3E28] underline">Google Cloud Console</a></li>
                  <li>Create a project and enable <strong>Google Calendar API</strong></li>
                  <li>Go to OAuth consent screen → External → Add scopes: <code className="bg-[#F5EDE6] px-1 py-0.5 rounded text-[10px]">calendar</code></li>
                  <li>Create OAuth credentials (Web application)</li>
                  <li>Set redirect URI to: <code className="bg-[#F5EDE6] px-1 py-0.5 rounded text-[10px] break-all">{window.location.origin}/api/oauth/calendar/callback</code></li>
                  <li>Add <code className="bg-[#F5EDE6] px-1 py-0.5 rounded text-[10px]">GOOGLE_CALENDAR_CLIENT_ID</code> and <code className="bg-[#F5EDE6] px-1 py-0.5 rounded text-[10px]">GOOGLE_CALENDAR_CLIENT_SECRET</code> to the backend</li>
                </ol>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-[#E8DDD4] bg-white" style={{ boxShadow: '0 2px 12px rgba(106,62,40,0.06)' }}>
        <CardContent className="p-6 space-y-5">
          <h3 className="text-lg font-semibold text-[#3D2418]">Operating Hours</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-sm">Start Time</Label><Input type="time" value={settings.operating_hours?.start || '09:00'} onChange={(e) => setSettings({ ...settings, operating_hours: { ...settings.operating_hours, start: e.target.value } })} className="mt-1 rounded-lg" data-testid="settings-start-time" /></div>
            <div><Label className="text-sm">End Time</Label><Input type="time" value={settings.operating_hours?.end || '18:00'} onChange={(e) => setSettings({ ...settings, operating_hours: { ...settings.operating_hours, end: e.target.value } })} className="mt-1 rounded-lg" data-testid="settings-end-time" /></div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-[#E8DDD4] bg-white" style={{ boxShadow: '0 2px 12px rgba(106,62,40,0.06)' }}>
        <CardContent className="p-6 space-y-5">
          <h3 className="text-lg font-semibold text-[#3D2418]">Booking Settings</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-sm">Buffer Time (minutes)</Label><Input type="number" value={settings.buffer_time || 15} onChange={(e) => setSettings({ ...settings, buffer_time: parseInt(e.target.value) })} className="mt-1 rounded-lg" data-testid="settings-buffer" /></div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={settings.deposit_required || false} onCheckedChange={(c) => setSettings({ ...settings, deposit_required: c })} data-testid="settings-deposit-switch" />
              <Label className="text-sm">Require Deposit</Label>
            </div>
          </div>
          <div>
            <Label className="text-sm">Cancellation Policy</Label>
            <Textarea value={settings.cancellation_policy || ''} onChange={(e) => setSettings({ ...settings, cancellation_policy: e.target.value })} className="mt-1 rounded-lg" rows={3} data-testid="settings-cancel-policy" />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button className="rounded-full bg-[#6A3E28] text-white hover:bg-[#5A3220] shadow-md px-8" onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
}
